import chance from '@/modules/base/services/chance';
import {Player, Team} from "@/modules/team/entity";
import {PositionEnum} from "@/modules/base/enums";
import {inject, injectable} from "inversify";
import CountryRandomizer from "@/modules/base/services/CountryRandomizer";
import {EventEmitter} from "@/modules/base/events";
import {AfterPlayerCreatedEvent} from "@/modules/team/events/AfterPlayerCreatedEvent";
import WageCalculator from "@/modules/team/services/WageCalculator";
import MarketValueCalculator from "@/modules/team/services/MarketValueCalculator";
import NameGenerator from "@/modules/team/services/NameGenerator";
import {Country, Region} from "@/modules/base/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";

@injectable()
export default class FastPlayerFactory {
  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(CountryRandomizer) private countryRandomizer: CountryRandomizer,
    @inject(WageCalculator) private wageCalculator: WageCalculator,
    @inject(MarketValueCalculator) private marketValueCalculator: MarketValueCalculator,
    @inject(NameGenerator) private nameGenerator: NameGenerator,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {}

  private players: Player[] = [];

  // if team does not have enough players it will generate new to will the positions
  async generateYoungFiller(team: Team, options: { callback?: (player: Player) => void } = {}) {
    const otherRegions = await Region
      .createQueryBuilder('region')
      .where(`region.id != :regionId`, {regionId: team.country.region.id})
      .getMany();

    const isForeigner = chance.bool({likelihood: this.configRepository.get('teams.percentageForeigners')(team)});
    const isFromSameRegion = chance.bool({likelihood: 100 - (collect(otherRegions).sum('percentageAbroad') as number)});

    return await this.generate({
      level: team.level,
      country: !isForeigner ? team.country : undefined,
      regions: isForeigner && isFromSameRegion ? [team.country.region] : otherRegions,
      callback: async (player: Player) => {
        player.team = team;
        player.skill = player.skill * .5;

        if (options.callback) {
          await options.callback(player);
        }
      }
    });
  }

  async generate(options: { country?: Country, regions?: Region[], level: number, callback?: (player: Player) => void }) {
    let country = options.country;

    if (!country) {
      const region = chance.pickoneByFieldProbability(options.regions || [], (r: Region) => r.percentageAbroad);
      country = await this.countryRandomizer.one(region);
    }

    const player = new Player();

    player.name = this.nameGenerator.generate(country.name);
    player.age = chance.integer({min: 15, max: 35});
    player.talent = chance.integer({min: .7 * options.level, max: 1.1 * options.level});
    player.skill = chance.integer({min: .8 * options.level, max: options.level});
    player.position = chance.randomEnum(PositionEnum);
    player.country = country;
    player.patience = chance.integer({min: 1, max: 100});
    player.greediness = chance.integer({min: 1, max: 100});
    player.resentfulness = chance.integer({min: 1, max: 100});
    player.wage = this.wageCalculator.calculate(player);
    player.contractYears = chance.integer({min: 1, max: 5});
    player.endsCareer = chance.bool({likelihood: 10});
    player.marketValue = await this.marketValueCalculator.calculate(player);

    if (options.callback) {
      await options.callback(player);
    }

    this.players.push(player);

    return player;
  }

  async flush() {
    await Player.createQueryBuilder('player').insert().into(Player).values(this.players).execute();
    // const players = await db.getRepository(Player).save(this.players);

    await this.players.forEachAsync(async (player: Player) => {
      await this.em.emitAsync(new AfterPlayerCreatedEvent(player));
    });

    this.players = [];
  }
}
